import {TargetBlank} from '@wandb/weave/common/util/links';
import {list, varNode} from '@wandb/weave/core';
import React from 'react';

import {lookupDocUrl} from '../../../util/doc_urls';
import {
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_EVALUATION_NAME,
  PANEL_BANK_MEDIA_NAME,
} from '../../../util/panelbankTypes';
import {PanelConfig} from '../../../util/panelTypes';
import {PanelViews, panelViews} from '../../PanelBank/types';
import {INITIAL_CONFIG as VEGA2_INITIAL_CONFIG} from '../../PanelVega2';
import type {
  AddPanelsMenuGroupProps,
  AddPanelsMenuItemProps,
  PanelType,
} from './types';

const QUICK_ADD_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'quick-add',
    iconName: 'panel-auto-gen',
    label: 'Quick add',
    description: 'Easily add panels from available keys.',
  },
];

const CHARTS_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'line',
    iconName: 'line-plot-alt2',
    label: 'Line plot',
    description: 'Display data trends and changes over time.',
  },
  {
    type: 'bar',
    iconName: 'chart-vertical-bars',
    label: 'Bar chart',
    description: 'Compare categories using vertical or horizontal bars.',
  },
  {
    type: 'scatter',
    iconName: 'chart-scatterplot',
    label: 'Scatter plot',
    description: 'Display relationships and correlations between data.',
  },
  {
    type: 'parallel',
    iconName: 'parallel-coordinates',
    label: 'Parallel coordinates',
    description: 'Compare multiple parameters across several dimensions.',
    isMultiRunOnly: true,
  },
  // items below should be hidden under "show more" by default
  {
    type: 'vega2',
    iconName: 'category-multimodal',
    label: 'Custom chart',
    description: 'Build custom visualizations using Vega.',
    isHiddenDefault: true,
  },
  {
    type: 'plotly',
    iconName: 'plotly',
    label: 'Plotly',
    description: 'Display tailored visualizations using Plotly.',
    isHiddenDefault: true,
  },
  {
    type: 'scalar',
    iconName: 'type-number-alt',
    label: 'Scalar chart',
    description: 'Display a single numeric value or metric.',
    isHiddenDefault: true,
  },
];

const EVALUATION_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'comparer',
    iconName: 'table',
    label: 'Run comparer',
    description: 'Compare metrics across runs and highlight differences.',
    isMultiRunOnly: true,
  },
  {
    type: 'parameter-importance',
    iconName: 'code-alt',
    label: 'Parameter importance',
    description: 'Compare hyper-parameters linked to desired outcomes.',
  },
];

const MEDIA_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'object3D',
    iconName: 'cube-container',
    label: '3D objects',
    description: 'Display 3D objects.',
  },
  {
    type: 'image',
    iconName: 'photo',
    label: 'Images',
    description: 'Display image data.',
  },
  {
    type: 'video',
    iconName: 'play',
    label: 'Video',
    description: 'Display video data.',
  },
  {
    type: 'audio',
    iconName: 'audio-volume',
    label: 'Audio',
    description: 'Display audio data',
  },
];

const TEXT_CODE_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'code',
    iconName: 'code-alt',
    label: 'Code',
    description: 'Display and compare code diffs across runs.',
  },
  {
    type: 'html',
    iconName: 'code-alt',
    label: 'HTML',
    description: 'Display HTML content directly.',
  },
  {
    type: 'markdown',
    iconName: 'text-language',
    label: 'Markdown',
    description: 'Add formatted notes to track insights and observations.',
  },
];

const QUERY_PANEL_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'panel-weave',
    iconName: 'search',
    label: 'Query panel',
    description: 'Query and visualize data interactively.',
  },
  {
    type: 'weave-table',
    iconName: 'table',
    label: 'Table',
    description: (
      <>
        Display data logged with{' '}
        <code className="rounded bg-oblivion/[0.04] p-3 font-[Inconsolata]">
          wandb.Table()
        </code>{' '}
        as a table.
      </>
    ),
  },
  {
    type: 'weave-plot',
    iconName: 'chart-scatterplot',
    label: 'Scatter plot',
    description: (
      <>
        Display data logged with{' '}
        <code className="rounded bg-oblivion/[0.04] p-3 font-[Inconsolata]">
          wandb.Table()
        </code>{' '}
        as a scatter plot.
      </>
    ),
  },
  {
    type: 'weave-projector',
    iconName: 'play',
    label: 'Embedding projector',
    description: 'Display high-dimensional data as vectors.',
  },
];

const WEAVE_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'weave-trace',
    iconName: 'table',
    label: 'Weave Traces',
    description: 'View and analyze Weave traces for selected runs.',
  },
];

export const WEAVE_GROUP: AddPanelsMenuGroupProps = {
  title: 'Weave',
  iconColor: 'sienna',
  items: WEAVE_ITEMS,
  isExpandedDefault: true,
};

export const ADD_PANELS_MENU_GROUPS: AddPanelsMenuGroupProps[] = [
  {
    title: PANEL_BANK_CHARTS_NAME,
    iconColor: 'blue',
    items: CHARTS_ITEMS,
    isExpandedDefault: true,
  },
  {
    title: PANEL_BANK_EVALUATION_NAME,
    iconColor: 'purple',
    items: EVALUATION_ITEMS,
    isExpandedDefault: true,
  },
  {
    title: 'Query panels',
    description: (
      <>
        Learn more about query panels{' '}
        <TargetBlank href={lookupDocUrl('query-panel')}>
          in W&B docs
        </TargetBlank>
        .
      </>
    ),
    iconColor: 'magenta',
    items: QUERY_PANEL_ITEMS,
  },
  {
    title: PANEL_BANK_MEDIA_NAME,
    iconColor: 'green',
    items: MEDIA_ITEMS,
  },
  {
    title: 'Text and code',
    iconColor: 'teal',
    items: TEXT_CODE_ITEMS,
  },
];

export const ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD: AddPanelsMenuGroupProps[] =
  [
    {
      items: QUICK_ADD_ITEMS,
      iconColor: 'cactus',
    },
    ...ADD_PANELS_MENU_GROUPS,
  ];

export const PANEL_VIEWS_BY_PANEL_TYPE: Partial<Record<PanelType, PanelViews>> =
  {
    bar: panelViews.barChart,
    code: panelViews.codeComparer,
    comparer: panelViews.runComparer,
    line: panelViews.runHistoryLinePlot,
    markdown: panelViews.markdown,
    'panel-weave': panelViews.weave,
    parallel: panelViews.parallelCoordinatesPlot,
    'parameter-importance': panelViews.parameterImportance,
    scalar: panelViews.scalarChart,
    scatter: panelViews.scatterPlot,
    vega2: panelViews.vega2,
    'weave-plot': panelViews.weave,
    'weave-projector': panelViews.weave,
    'weave-table': panelViews.weave,
    'weave-trace': panelViews.weaveTrace,
  };

export const DEFAULT_CONFIGS_BY_PANEL_TYPE: Partial<
  Record<PanelType, PanelConfig>
> = {
  vega2: VEGA2_INITIAL_CONFIG,
  'weave-plot': {
    panel2Config: {
      exprAndPanelLocked: true,
      panelId: 'runs-table.row.plot',
      exp: varNode(list('run' as const), 'runs'),
    },
  },
  'weave-projector': {
    panel2Config: {
      exprAndPanelLocked: true,
      panelId: 'runs-table.row.projection.plot',
      exp: varNode(list('run' as const), 'runs'),
    },
  },
  'weave-table': {
    panel2Config: {
      exprAndPanelLocked: true,
      panelId: 'runs-table.row.table',
      exp: varNode(list('run' as const), 'runs'),
    },
  },
};
